import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { KeyRound, Loader2, Eye, EyeOff } from "lucide-react";

/**
 * Set your own password.
 *
 * Used two ways, and the difference is only what it says and whether it can
 * be left. `forced` is the first login after the tutor made or reset the
 * account: the password in play is one a second person read off a screen and
 * typed into a message, so it is a way in rather than a password, and the
 * child cannot reach the rest of the portal until they have replaced it.
 * Without `forced` it is the same form reached by choice from the menu.
 *
 * **The old password is asked for even when the change is forced.** It is the
 * only proof available that the person at the keyboard is the one the login
 * was issued to - there is no email loop to fall back on, because a child's
 * login address is a username the tutor invented and need not be a real
 * mailbox. A forced screen that skipped it would let anyone who picked up an
 * unlocked phone take the account.
 *
 * **It cannot be dismissed by navigating.** The gate is enforced in the API,
 * in `get_current_user`, so a bookmark straight to a worksheet gets a 403
 * rather than a worksheet. The screen is the polite half of a rule that holds
 * without it.
 */
export default function ChangePassword({ forced = false }) {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const home = user?.role === "tutor" ? "/admin" : user?.role === "parent" ? "/parent" : "/student";

  const go = (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("Your new password needs at least 8 characters");
    if (next !== again) return toast.error("The two new passwords are not the same");
    setSaving(true);
    api.post("/auth/password", { current_password: current, new_password: next })
      .then(async () => {
        toast.success("That is your password now. Nobody else has it.");
        await refresh();
        navigate(home, { replace: true });
      })
      .catch((err) => toast.error(err.response?.data?.detail || "Could not change it"))
      .finally(() => setSaving(false));
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-start justify-center p-4">
      <div className="w-full max-w-md my-10">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-200">
            <div className="flex items-center gap-2.5 mb-1">
              <span className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
                <KeyRound size={16} className="text-slate-600" />
              </span>
              <h1 className="font-serif text-xl brand-navy">
                {forced ? "Choose your own password" : "Change your password"}
              </h1>
            </div>
            <p className="text-sm text-slate-500">
              {forced
                ? <>Hello {user?.name?.split(" ")[0]}. The password you were given
                    was sent to you in a message, so somebody else has seen it.
                    Pick one only you know and the portal will open.</>
                : <>Type the password you use now, then the one you would like
                    instead.</>}
            </p>
          </div>

          <form onSubmit={go} className="p-6 space-y-4">
            <label className="block text-sm">
              <span className="block text-slate-600 mb-1">
                {forced ? "The password you were given" : "Your password now"}
              </span>
              <input type={show ? "text" : "password"} value={current}
                onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password"
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5" />
            </label>

            <label className="block text-sm">
              <span className="block text-slate-600 mb-1">Your new password</span>
              <input type={show ? "text" : "password"} value={next}
                onChange={(e) => setNext(e.target.value)} autoComplete="new-password"
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5" />
              <span className="block text-xs text-slate-400 mt-1">
                At least 8 characters. Three words you will remember beats one
                clever word you will not.
              </span>
            </label>

            <label className="block text-sm">
              <span className="block text-slate-600 mb-1">Type it once more</span>
              <input type={show ? "text" : "password"} value={again}
                onChange={(e) => setAgain(e.target.value)} autoComplete="new-password"
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5" />
            </label>

            <button type="button" onClick={() => setShow((x) => !x)}
              className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700">
              {show ? <EyeOff size={12} /> : <Eye size={12} />}
              {show ? "Hide what I am typing" : "Show what I am typing"}
            </button>

            <button type="submit" disabled={saving}
              className="w-full bg-brand-navy text-white font-semibold rounded-lg py-2.5
                         disabled:opacity-50 flex items-center justify-center gap-2">
              {saving && <Loader2 size={15} className="animate-spin" />}
              {saving ? "Saving…" : "Save my new password"}
            </button>

            {/* A way out that is honest about itself. There is no "skip":
                skipping is what the API refuses. Logging out is the only
                other thing a person in this state can actually do, and
                hiding it leaves them with a form and no exit. */}
            <div className="pt-1 text-center">
              {forced ? (
                <button type="button" onClick={() => { logout(); navigate("/login", { replace: true }); }}
                  className="text-xs text-slate-400 hover:text-slate-600">
                  Not you? Log out
                </button>
              ) : (
                <button type="button" onClick={() => navigate(home)}
                  className="text-xs text-slate-500 hover:text-slate-700">
                  Leave it as it is
                </button>
              )}
            </div>
          </form>
        </div>

        {forced && (
          <p className="text-xs text-slate-400 text-center mt-4">
            Forgotten the password you were sent? Ask Genius Education and they
            will issue a new one.
          </p>
        )}
      </div>
    </div>
  );
}
